import { useState } from "react";
import axios from "axios";
import Image from "next/image";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { Lock, SlidersHorizontal } from "lucide-react";
import { ModalSectionLayout } from "./ModalSectionLayout";
import { Switch } from "../../shadcn/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../shadcn/tooltip";
import { SpotToast } from "../../SpotToast";
import { Badge } from "../../shadcn/badge";
import { Button } from "../../shadcn/button";
import { cn } from "@/lib/utils";
import type { ActiveCredential } from "./types";

export function CredentialCustomizationPanel({
  cred,
  groupId,
  onUpdate,
  onCredChange,
}: {
  cred: ActiveCredential;
  groupId: string;
  onUpdate: () => void;
  onCredChange: (updated: ActiveCredential) => void;
}) {
  const queryClient = useQueryClient();
  const nodes = cred.integration.nodes.filter((n) => n.visible);
  const triggers = cred.integration.triggers.filter((t) => t.visible);

  // Local optimistic state — avoids the parent useEffect reverting updates
  const [disabledNodes, setDisabledNodes] = useState<string[]>(
    cred.disabledNodes ?? []
  );
  const [disabledTriggers, setDisabledTriggers] = useState<string[]>(
    cred.disabledTriggers ?? []
  );

  const { mutate: saveSettings } = useMutation({
    mutationFn: async ({
      previousNodes: _pn,
      previousTriggers: _pt,
      ...body
    }: {
      disabled_nodes?: string[];
      disabled_triggers?: string[];
      previousNodes: string[];
      previousTriggers: string[];
    }) => {
      return axios.put(
        `/api/backend/groups/${groupId}/integrations/credentials/${cred.credentialId}/settings`,
        body
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.INTEGRATIONS_CONFIG],
      });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.INTEGRATIONS] });
      onUpdate();
    },
    onError: (_, { previousNodes, previousTriggers }) => {
      setDisabledNodes(previousNodes);
      setDisabledTriggers(previousTriggers);
      SpotToast.error("Failed to update settings.");
    },
  });

  const toggleNode = (nodeName: string, currentlyEnabled: boolean) => {
    const previousNodes = disabledNodes;
    const next = currentlyEnabled
      ? [...disabledNodes, nodeName]
      : disabledNodes.filter((n) => n !== nodeName);
    setDisabledNodes(next);
    saveSettings({
      disabled_nodes: next,
      previousNodes,
      previousTriggers: disabledTriggers,
    });
  };

  const toggleTrigger = (triggerName: string, currentlyEnabled: boolean) => {
    const previousTriggers = disabledTriggers;
    const next = currentlyEnabled
      ? [...disabledTriggers, triggerName]
      : disabledTriggers.filter((t) => t !== triggerName);
    setDisabledTriggers(next);
    saveSettings({
      disabled_triggers: next,
      previousNodes: disabledNodes,
      previousTriggers,
    });
  };

  return (
    <ModalSectionLayout
      title="Customization"
      description="Enable or disable specific nodes and triggers for this connection."
    >
      <div className="space-y-6">
        {nodes.length > 0 && (
          <div>
            <span className="text-sm font-medium mb-3 block leading-none">
              Nodes
            </span>
            <div className="flex flex-col gap-3">
              {nodes.map((node) => {
                const adminDisabled = !node.admin_enabled;
                const credDisabled = disabledNodes.includes(node.node_name);
                const isEnabled = !adminDisabled && !credDisabled;

                return (
                  <div
                    key={node.node_name}
                    onClick={
                      adminDisabled
                        ? undefined
                        : () => toggleNode(node.node_name, isEnabled)
                    }
                    className={cn(
                      "flex items-start justify-between p-2.5 rounded-lg border transition-colors gap-4",
                      adminDisabled
                        ? "bg-muted/20 border-dashed border-border/30 opacity-60"
                        : "bg-card border-border cursor-pointer"
                    )}
                  >
                    <div className="flex items-start gap-2.5 min-w-0">
                      {node.image && (
                        <div className="shrink-0 w-8 h-8 rounded-md bg-background border border-border/40 flex items-center justify-center p-1 mt-0.5">
                          <Image
                            width={16}
                            height={16}
                            alt={node.title}
                            src={node.image}
                            style={{
                              objectFit: "contain",
                              width: "100%",
                              height: "100%",
                            }}
                          />
                        </div>
                      )}
                      <div className="min-w-0">
                        <span className="text-sm font-medium truncate block">
                          {node.title}
                        </span>
                        <div className="h-8 overflow-hidden">
                          <span className="text-xs text-muted-foreground line-clamp-2 block">
                            {node.description ?? "\u00A0"}
                          </span>
                        </div>
                      </div>
                    </div>
                    {adminDisabled ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="shrink-0 ml-2 mt-1">
                            <Lock
                              size={14}
                              className="text-muted-foreground/50"
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Disabled by admin</TooltipContent>
                      </Tooltip>
                    ) : (
                      <div
                        className="flex items-start justify-end mt-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() =>
                            toggleNode(node.node_name, isEnabled)
                          }
                          className="shrink-0 ml-2"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {triggers.length > 0 && (
          <div>
            <span className="text-sm font-medium mb-3 block">Triggers</span>
            <div className="flex flex-col gap-3">
              {triggers.map((trigger) => {
                const adminDisabled = !trigger.admin_enabled;
                const credDisabled = disabledTriggers.includes(
                  trigger.trigger_name
                );
                const isEnabled = !adminDisabled && !credDisabled;

                return (
                  <div
                    key={trigger.trigger_name}
                    onClick={
                      adminDisabled
                        ? undefined
                        : () => toggleTrigger(trigger.trigger_name, isEnabled)
                    }
                    className={cn(
                      "flex items-center justify-between p-2.5 rounded-lg border transition-colors",
                      adminDisabled
                        ? "bg-muted/20 border-dashed border-border/30 opacity-60"
                        : "bg-card border-border/60 cursor-pointer"
                    )}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      {trigger.image && (
                        <div className="shrink-0 w-7 h-7 rounded-md bg-background border border-border/40 flex items-center justify-center p-1">
                          <Image
                            width={16}
                            height={16}
                            alt={trigger.title}
                            src={trigger.image}
                            style={{
                              objectFit: "contain",
                              width: "100%",
                              height: "100%",
                            }}
                          />
                        </div>
                      )}
                      <div className="min-w-0">
                        <span className="text-[13px] font-medium truncate block">
                          {trigger.title}
                        </span>
                        {trigger.description && (
                          <span className="text-[11px] text-muted-foreground truncate block">
                            {trigger.description}
                          </span>
                        )}
                      </div>
                    </div>
                    {adminDisabled ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="shrink-0 ml-2">
                            <Lock
                              size={14}
                              className="text-muted-foreground/50"
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Disabled by admin</TooltipContent>
                      </Tooltip>
                    ) : (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() =>
                            toggleTrigger(trigger.trigger_name, isEnabled)
                          }
                          className="shrink-0 ml-2"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {nodes.length === 0 && triggers.length === 0 && (
          <div className="text-center py-8">
            <SlidersHorizontal
              size={32}
              className="mx-auto text-muted-foreground/40 mb-2"
            />
            <p className="text-sm text-muted-foreground">
              No customizable entities for this integration
            </p>
          </div>
        )}
      </div>
    </ModalSectionLayout>
  );
}
